import {
  Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  ContractWorkflowService,
  TaskDetailResponse,
  TaskActionRequest,
  WorkflowStatusResponse,
  CommentDto,
  UserDto,
  ParticipantDto
} from './contract-workflow.service';
import { ContractService } from '../contracts/contract.service';


import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'; 


@Component({
  selector: 'app-contract-workflow-detail',
  templateUrl: './contract-workflow-detail.component.html',
  styleUrls: ['./contract-workflow-detail.component.scss']
})
export class ContractWorkflowDetailComponent implements OnInit, OnDestroy {

  @ViewChild('docPreview', { static: false }) docPreviewEl!: ElementRef<HTMLDivElement>;
  @ViewChild('pdfFrame',   { static: false }) pdfFrameEl!:   ElementRef<HTMLIFrameElement>;

  // ── State ──────────────────────────────────────────────────────

  taskId = '';
  task: TaskDetailResponse | null = null;
  loading = true;
  submitting = false;
  error = '';

  // Decision form
  decision = '';
  comments = '';

  // Document panel
  docHtml = '';
  docLoading = false;
  zoomLevel = 1.0;
  activeTab: 'comments' | 'metadata' | 'history'| 'signatures' = 'comments';

  // OnlyOffice editor
  private ooEditor: any = null;
  ooScriptLoaded = false;

  // Signing state
  hasSigned = false;       // true once the signing popup was closed (not necessarily signed)
  signingInProgress = false;

  // ── Forward for Review panel ──────────────────────────────────
  showForwardPanel      = false;
  forwardSearch         = '';
  forwardSearchResults: UserDto[] = [];
  forwardSearching      = false;
  forwardSelectedUsers: UserDto[] = [];
  forwardComments       = '';
  isForwarding          = false;
  forwardError          = '';
  private forwardSearch$ = new Subject<string>();

  // Comment form
  newComment = '';
  replyingToId: number | null = null;

  // PDF blob URL (avoids 401 — same pattern as document library viewer)
  private _pdfSafeUrl: SafeResourceUrl | null = null;
  private _pdfObjectUrl: string | null = null;

  private destroy$ = new Subject<void>();
  //private _alfrescoBase = environment.alfrescoUrl;  // still used by openOnlyOffice

  constructor(
    private route: ActivatedRoute,
    public  router: Router,
    private workflowService: ContractWorkflowService,
    private contractService: ContractService,
   // private auth: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer  
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────

  ngOnInit(): void {
    // Wire forward-search debounce — avoids hammering the API on every keystroke
    this.forwardSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(q => this._doUserSearch(q));

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.taskId = params.get('taskId') ?? '';
      if (this.taskId) this.loadTask();
    });
  }

  ngOnDestroy(): void {
    this.destroyOnlyOfficeEditor();
    this.destroy$.next();
    this.destroy$.complete();
    if (this._pdfObjectUrl) {
      URL.revokeObjectURL(this._pdfObjectUrl);
      this._pdfObjectUrl = null;
    }
  }

  /**
   * Safe getter for the PDF iframe [src] binding.
   * Returns the blob: URL created by loadPdfBlob(), or null while loading.
   * HTML template continues to bind [src]="safePdfUrl" — no template changes needed.
   */
  get safePdfUrl(): SafeResourceUrl | null {
    return this._pdfSafeUrl;
  }

  // ── Load Task ──────────────────────────────────────────────────

  loadTask(): void {
    this.loading = true;
    this.error = '';
    this.workflowService.getTaskDetail(this.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: task => {
          this.task = task;
          this.loading = false;
          this.loadDocument();
          this.cdr.detectChanges();
        },
        error: err => {
          this.error = 'Failed to load task. ' + (err.message ?? '');
          this.loading = false;
        }
      });
  }

  // ── Document Preview ───────────────────────────────────────────

  loadDocument(): void {
    if (!this.task) return;
    this.docLoading = true;

    if (this.isApprovalTask) {
      // PDF stage: fetch via HttpClient so Bearer token is sent, then create blob URL.
      // Direct iframe src against Alfresco would cause 401 (no auth header on iframe requests).
      if (this.task.pdfNodeId) {
        this.loadPdfBlob(this.task.pdfNodeId);
      } else {
        this.docLoading = false;
        this.cdr.detectChanges();
      }
      return;
    }

    // Review stage: open in OnlyOffice editor
    this.loadOnlyOfficeEditor();
  }

  /**
   * Fetch a PDF node from Alfresco via the Spring Boot proxy (Bearer token attached by HttpClient).
   * Creates a blob: URL that the iframe can render without any auth challenge.
   * Same pattern used by the document-library viewer component.
   */
  private loadPdfBlob(nodeId: string): void {
    this.docLoading = true;

    this.contractService.downloadAlfrescoNode(nodeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          // Revoke any previous blob URL to avoid memory leak
          if (this._pdfObjectUrl) {
            URL.revokeObjectURL(this._pdfObjectUrl);
          }
          this._pdfObjectUrl = URL.createObjectURL(blob);
          this._pdfSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this._pdfObjectUrl);
          this.docLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('PDF load failed:', err);
          this._pdfSafeUrl = null;
          this.docLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * After completing a SIGN task, reload the document from Alfresco.
   * The annotator uploaded a new version — Alfresco returns the latest
   * version automatically for the same nodeId.
   */
  reloadDocumentAfterSigning(): void {
    if (!this.task?.pdfNodeId) return;
    // Re-fetch blob — Alfresco serves the latest version for the same nodeId
    this.loadPdfBlob(this.task.pdfNodeId);
  }

  // ── Computed Helpers ───────────────────────────────────────────

  get isReviewTask():   boolean { return this.task?.taskType === 'REVIEW'; }
  get isApprovalTask(): boolean { return this.task?.taskType === 'APPROVAL'; }
  get isArchiveTask():  boolean { return this.task?.taskType === 'ARCHIVE'; }
  get isConfigureTask():boolean { return this.task?.taskType === 'CONFIGURE'; }
  get isAmendTask():    boolean { return this.task?.taskType === 'AMEND'; }

  get progressPct(): number {
    if (!this.task) return 0;
    if (this.isReviewTask)   return this.task.reviewTotal   > 0 ? (this.task.reviewStep   / this.task.reviewTotal)   * 100 : 0;
    if (this.isApprovalTask) return this.task.approvalTotal > 0 ? (this.task.approvalStep / this.task.approvalTotal) * 100 : 0;
    return 0;
  }

  get stageBadgeColor(): string {
    return { REVIEW: '#4A90D9', APPROVAL: '#28a745', ARCHIVE: '#fd7e14',
             CONFIGURE: '#6f42c1', AMEND: '#fd7e14', REJECTED: '#dc3545' }[this.task?.taskType ?? ''] ?? '#1B3A6B';
  }

  get canSubmit(): boolean {
    if (!this.decision) return false;
    if ((this.decision === 'REJECT' || this.decision === 'REQUEST_CHANGES') && !this.comments.trim()) return false;
    if (this.isApprovalTask && this.decision === 'SIGN' && !this.hasSigned) return false;
    return true;
  }

  // ── Signing ────────────────────────────────────────────────────

  /**
   * Opens the SkyPlus Annotator popup for signing the contract PDF.
   * After the popup closes, sets decision='SIGN' and reloads the
   * PDF panel so the signed version (uploaded by the annotator to
   * Alfresco) is shown immediately.
   *
   * Mirrors SkyPlusDocumentLibraryComponent.signDocument() — the URL is
   * built on the frontend from the Alfresco nodeId and the authenticated
   * ECM username. Prefers pdfNodeId (the rendered PDF); falls back to
   * contractNodeId if no PDF has been generated yet.
   */
  signDocument(): void {
    const nodeId = this.task?.pdfNodeId || this.task?.contractNodeId;
    if (!nodeId) { this.error = 'No document available to sign.'; return; }
    this.signingInProgress = true;
    this.workflowService.signDocument(nodeId, () => {
      // Annotator popup closed — user either signed or cancelled
      this.signingInProgress = false;
      this.hasSigned = true;
      this.decision = 'SIGN';
      // Reload the document panel to show the signed PDF version from Alfresco
      this.reloadDocumentAfterSigning();
      this.cdr.detectChanges();
    });
  }

  // ── Submit Decision ────────────────────────────────────────────

  submitDecision(): void {
    if (!this.canSubmit || this.submitting) return;
    this.submitting = true;
    this.error = '';

    const request: TaskActionRequest = {
      decision:   this.decision,
      comments:   this.comments.trim() || undefined,
    };

    this.workflowService.completeTask(this.taskId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status: WorkflowStatusResponse) => {
          this.submitting = false;
          // Navigate back to the task list for the current task type
          this.router.navigate(['/workflow'], {
            queryParams: { section: this.getReturnSection(status) }
          });
        },
        error: err => {
          this.submitting = false;
          this.error = err.error?.message ?? 'Failed to submit decision.';
        }
      });
  }

  // ── Comments ───────────────────────────────────────────────────

  submitComment(): void {
    if (!this.newComment.trim() || !this.task) return;
     this.error = '';
    this.workflowService.addComment(
      this.taskId, this.task.contractNodeId, this.newComment.trim(), this.replyingToId ?? undefined
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: comment => {
        if (!this.task) return;
        if (this.replyingToId) {
          const parent = this.task.comments.find(c => c.id === this.replyingToId);
          if (parent) { parent.replies = [...(parent.replies ?? []), comment]; }
        } else {
          this.task.comments = [comment, ...this.task.comments];
        }
        this.newComment = '';
        this.replyingToId = null;
        this.cdr.detectChanges();
      },
      error: err => {
          this.error = err.error?.message ?? 'Failed to add comment.'
      }
    });
  }

  cancelReply(): void { this.replyingToId = null; }

  replyTo(comment: CommentDto): void {
    this.replyingToId = comment.id;
    this.activeTab = 'comments';
  }

  

  // ── Zoom ───────────────────────────────────────────────────────

  zoomIn():    void { this.zoomLevel = Math.min(this.zoomLevel + 0.1, 2.0); }
  zoomOut():   void { this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.5); }
  resetZoom(): void { this.zoomLevel = 1.0; }

  // ── Helpers ────────────────────────────────────────────────────

  private getReturnSection(_status: WorkflowStatusResponse): string {
    if (this.isReviewTask)    return 'cw-review';
    if (this.isApprovalTask)  return 'cw-approval';
    if (this.isArchiveTask)   return 'cw-complete';
    return 'cw-review';
  }

  trackByComment(_idx: number, c: CommentDto): number { return c.id; }

  formatDate(d?: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  /**
   * Normalise raw backend status values → canonical display statuses.
   *
   * The backend/Flowable/Alfresco may return various strings:
   *   APPROVED, SIGNED, REVIEWED, COMPLETED → completed
   *   IN_REVIEW, in_review                  → in_review
   *   IN_APPROVAL, in_approval              → in_approval
   *   REJECTED, DECLINED                    → rejected
   *   CHANGES_REQUESTED                     → changes_requested
   *   anything else                         → pending
   */
  normalizeStatus(raw: string): string {
    if (!raw) return 'pending';
    const s = raw.toUpperCase();
    if (['APPROVED', 'SIGNED', 'REVIEWED', 'COMPLETED', 'DONE'].includes(s)) return 'completed';
    if (['IN_REVIEW', 'INREVIEW'].includes(s))                               return 'in_review';
    if (['IN_APPROVAL', 'INAPPROVAL'].includes(s))                           return 'in_approval';
    if (['REJECTED', 'DECLINED'].includes(s))                                return 'rejected';
    if (['CHANGES_REQUESTED', 'CHANGESREQUESTED'].includes(s))               return 'changes_requested';
    return 'pending';
  }

  /** Human-readable label for a raw backend status value. */
  getStatusLabel(raw: string): string {
    const norm = this.normalizeStatus(raw);
    return {
      completed:         'Completed',
      in_review:         'In Review',
      in_approval:       'In Progress',
      rejected:          'Rejected',
      changes_requested: 'Changes Requested',
      pending:           'Pending',
    }[norm] ?? 'Pending';
  }

  getParticipantIcon(status: string): string {
    return {
      completed:         'check_circle',
      in_review:         'radio_button_checked',
      in_approval:       'radio_button_checked',
      rejected:          'cancel',
      changes_requested: 'edit',
      pending:           'radio_button_unchecked'
    }[status] ?? 'help';
  }

  getParticipantColor(status: string): string {
    return {
      completed:         '#28a745',
      in_review:         '#4A90D9',
      in_approval:       '#4A90D9',
      rejected:          '#dc3545',
      changes_requested: '#fd7e14',
      pending:           '#c5d5ea'
    }[status] ?? '#7a8fa6';
  }

  /**
   * Load the OnlyOffice editor inside the document panel.
   * 1. Injects api.js from the Document Server (once)
   * 2. Fetches the signed config from our backend
   * 3. Initialises DocsAPI.DocEditor in the #oo-container div
   */
  private loadOnlyOfficeEditor(): void {
    if (!this.task) return;
    this.docLoading = true;

    this.workflowService.getOnlyOfficeConfig(this.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: config => {
          this.ensureOnlyOfficeScript(config);
        },
        error: _err => {
          this.docHtml = '<p style="color:red">Failed to load editor config.</p>';
          this.docLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private ensureOnlyOfficeScript(config: any): void {
    const scriptId = 'onlyoffice-api-js';
    if (document.getElementById(scriptId)) {
      // Script already loaded — init editor immediately
      this.initOnlyOfficeEditor(config);
      return;
    }
    const script    = document.createElement('script');
    script.id       = scriptId;
    script.src      = `${config._documentServerUrl}/web-apps/apps/api/documents/api.js`;
    script.onload   = () => this.initOnlyOfficeEditor(config);
    script.onerror  = () => {
      this.docHtml    = '<p style="color:red">Could not load OnlyOffice Document Server script. Check server URL.</p>';
      this.docLoading = false;
      this.cdr.detectChanges();
    };
    document.head.appendChild(script);
  }

  private initOnlyOfficeEditor(config: any): void {
    // Destroy any previous editor instance to avoid duplicate iframes
    if (this.ooEditor && typeof this.ooEditor.destroyEditor === 'function') {
      this.ooEditor.destroyEditor();
      this.ooEditor = null;
    }

    const DocsAPI = (window as any).DocsAPI;
    if (!DocsAPI) {
      this.docHtml    = '<p style="color:red">DocsAPI not available.</p>';
      this.docLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Remove the _documentServerUrl helper field before passing to DocsAPI
    const { _documentServerUrl, ...editorConfig } = config;

    this.docLoading = false;
    this.cdr.detectChanges(); // render #oo-container div before initialising

    // Small timeout to ensure Angular has rendered the container
    setTimeout(() => {
      try {
        this.ooEditor = new DocsAPI.DocEditor('oo-container', {
          ...editorConfig,
          events: {
            onDocumentStateChange: (_event: any) => {
              // event.data === true means document has unsaved changes
            },
            onError: (event: any) => {
              console.error('OnlyOffice error:', event.data);
            }
          }
        });
      } catch (e) {
        console.error('DocsAPI.DocEditor init failed:', e);
      }
    }, 100);
  }

  /** Destroy editor on component teardown to avoid memory leaks */
  private destroyOnlyOfficeEditor(): void {
    if (this.ooEditor && typeof this.ooEditor.destroyEditor === 'function') {
      try { this.ooEditor.destroyEditor(); } catch (_) {}
      this.ooEditor = null;
    }
  }

  // ── Forward for Review ────────────────────────────────────────

  openForwardPanel(): void {
    this.showForwardPanel     = true;
    this.forwardSearch        = '';
    this.forwardSearchResults = [];
    this.forwardSelectedUsers = [];
    this.forwardComments      = '';
    this.forwardError         = '';
  }

  closeForwardPanel(): void {
    this.showForwardPanel = false;
    this.forwardError     = '';
  }

  onForwardSearchChange(): void {
    this.forwardSearch$.next(this.forwardSearch.trim());
  }

  private _doUserSearch(q: string): void {
    if (!q || q.length < 2) { this.forwardSearchResults = []; return; }
    this.forwardSearching = true;
    // Uses ContractService which carries the ADF Bearer token — no hardcoded credentials
    this.contractService.searchUsers(q)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: users => {
          const existingIds = new Set([
            ...this.forwardSelectedUsers.map(u => u.id),
            ...(this.task?.reviewers ?? []).map(r => r.id)
          ]);
          this.forwardSearchResults = users.filter(u => !existingIds.has(u.id));
          this.forwardSearching = false;
          this.cdr.detectChanges();
        },
        error: () => { this.forwardSearching = false; }
      });
  }

  addForwardUser(user: UserDto): void {
    if (!this.forwardSelectedUsers.find(u => u.id === user.id)) {
      this.forwardSelectedUsers = [...this.forwardSelectedUsers, user];
    }
    // Clear input AND full results list so user can search for someone else immediately
    this.forwardSearch = '';
    this.forwardSearchResults = [];
    this.cdr.detectChanges();
  }

  removeForwardUser(user: UserDto): void {
    this.forwardSelectedUsers = this.forwardSelectedUsers.filter(u => u.id !== user.id);
  }

  submitForward(): void {
    if (!this.forwardSelectedUsers.length || this.isForwarding) return;
    this.isForwarding = true;
    this.forwardError = '';

    const currentMax = (this.task?.reviewers ?? []).reduce((m, r) => Math.max(m, r.order), 0);
    const additionalReviewers: ParticipantDto[] = this.forwardSelectedUsers.map((u, i) => ({
      id:          u.id,
      displayName: u.displayName,
      email:       u.email ?? '',
      order:       currentMax + i + 1,
      status:      'pending'
    }));

    this.workflowService.forwardTask(this.taskId, {
      additionalReviewers,
      comments: this.forwardComments.trim() || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isForwarding = false;
        this.router.navigate(['/workflow'], { queryParams: { section: 'cw-review' } });
      },
      error: err => {
        this.isForwarding = false;
        this.forwardError = err?.error?.message ?? 'Failed to forward task. Please try again.';
      }
    });
  }

  isCurrentStep(index: number): boolean {
    if (this.isReviewTask)   return index === (this.task?.reviewStep ?? 0);
    if (this.isApprovalTask) return index === (this.task?.approvalStep ?? 0);
    return false;
  }
}